// 261005 E4-P4b — dải "Phần mới" dưới header: 6 bước + nút Xử lý phần mới / Chạy tiếp / Huỷ. Quyết định ở `appendRunView.ts`; file này chỉ vẽ + gọi API.
import React, { useState } from 'react';
import { Space, Tooltip, Typography, message } from 'antd';
import Button from './WbButton';
import { adaptationApi } from '../../../services/api/adaptation';
import type { ScanEstimate } from '../../../types/adaptation';
import {
  barKind, barTitle, cancelCopy, chainSteps, costLines, interruptedStripView, newEpisodeCount, startConfirmText,
  STEP_ICON, stepProgressText,
} from './appendRunView';
import { askConfirm, handleAppendError } from './appendRunActions';
import { isNewStoryRun } from './newStoryRunView';
import { episodePlanSaveErrorView, submitEpisodePlanApproval } from './episodePlanView';
import { useWorkbenchCtx } from './WorkbenchContext';
import BandCtaReporter from './BandCtaReporter';
import { Strip } from './WbSlot';
import { redactMoney } from './moneyRedact';
import { canApprove } from '../adaptationUtils';

const { Text } = Typography;

interface Props { dismissedId: string | null; onDismiss: (id: string) => void }

const AppendRunBar: React.FC<Props> = ({ dismissedId, onDismiss }) => {
  const { storyId, workbench, data, refreshAll, goTo, busy } = useWorkbenchCtx();
  const [working, setWorking] = useState(false);
  if (!workbench) return null;
  if (isNewStoryRun(workbench.appendRun)) return null;   // 261023 E3-P3c1: chuỗi truyện mới do `NewStoryRunBar` vẽ
  const kind = barKind(workbench, dismissedId);
  if (kind === 'none') return null;
  const run = workbench.appendRun ?? null;
  const steps = chainSteps({ appendRun: run, interrupted: !!workbench.interrupted });
  const progress = stepProgressText(run, workbench.jobs.running);
  const episodesListed = workbench.outlineFlags.episodesListed;
  const K = newEpisodeCount(run, episodesListed);
  const interrupted = interruptedStripView(run);
  const planApproval = canApprove(data.record?.outline ?? null, data.record?.violations);
  const savedErrorCount = (data.record?.violations ?? []).filter((v) => v.severity === 'ERROR').length;
  const planApproveDisabledReason = !planApproval.can ? `Còn ${savedErrorCount} lỗi phải xử` : undefined;

  const fail = (e: any) => handleAppendError(e, { goTo, refreshAll });

  const guarded = async (fn: () => Promise<void>) => {
    if (working) return;
    setWorking(true);
    try { await fn(); } catch (e) { await fail(e); } finally { setWorking(false); }
  };

  const start = () => guarded(async () => {
    let est: ScanEstimate | null = null;
    try { est = await adaptationApi.scanEstimate({ storyId }); } catch { est = null; }
    const lines = [...startConfirmText({ newChapters: workbench.newChapters ?? 0, range: workbench.newChapterRange, episodesListed, warnResplit: workbench.warnResplit ?? 0 }), ...costLines(est)];
    const ok = await askConfirm({ title: 'Xử lý phần mới?', okText: 'Xử lý phần mới', content: <div>{lines.map((l, i) => <p key={i} style={{ margin: '0 0 6px' }}>{l}</p>)}</div> });
    if (!ok) return;
    await adaptationApi.appendRunStart(storyId);
    message.success('Đang xử lý phần mới — chạy ở nền');
    await refreshAll();
  });

  const resume = () => guarded(async () => {
    await adaptationApi.appendRunContinue(storyId);
    await refreshAll();
  });

  // Chỗ dừng 2: BE đòi mốc `modifiedAt` của dàn tập người đang xem (thiếu ⇒ 400 EXPECTED_MODIFIED_AT_REQUIRED) — lấy từ bản ② đã tải ở trang, như nút chốt ở ④.
  const approvePlan = () => guarded(async () => {
    const modifiedAt = data.record?.modifiedAt;
    if (!modifiedAt) { message.warning('Dàn tập vừa đổi — đã tải lại, thử lại', 5); await refreshAll(); return; }
    try {
      await submitEpisodePlanApproval(adaptationApi, { storyId, modifiedAt, appendWaiting: true });
      message.success('Đã chốt dàn tập và bắt đầu viết tập mới');
      await refreshAll();
    } catch (e) {
      const v = episodePlanSaveErrorView(e, 'Không chốt được dàn tập');
      if (v.level === 'warning') message.warning(v.text, 6); else message.error(v.text, 6);
      if (v.exit === 'openOverview') goTo({ stage: 'skeleton', intent: 'openOverview' });
      await refreshAll();
    }
  });

  const cancel = () => guarded(async () => {
    if (!run) return;
    const c = cancelCopy(run);
    if (!(await askConfirm({ title: c.title, content: c.content, okText: c.okText, danger: c.danger }))) return;
    const r = await adaptationApi.appendRunCancel(storyId);
    const n = r.discardedVersions?.length ?? 0;
    if (r.warning) message.warning(redactMoney(r.warning), 8);
    else message.success(r.restored ? `Đã huỷ và hoàn tác lần gộp${n ? ` (bỏ ${n} phiên bản ②)` : ''}` : 'Đã dừng xử lý phần mới');
    await refreshAll();
  });

  const cancelBtn = (
    <Tooltip title={kind === 'running' ? 'Đang chạy — chờ bước này xong (hoặc tới điểm dừng) rồi huỷ' : undefined}>
      <Button size="small" danger disabled={working || kind === 'running'} onClick={cancel}>Huỷ phần mới</Button>
    </Tooltip>
  );

  return (
    <div className="wb-job" role="status" id="append-run-bar">
      <BandCtaReporter id="append" active={kind === 'start' ? !(busy || working) : kind === 'interrupted' ? !working : kind === 'wait' && run?.status === 'WAIT_EPISODE_PLAN' ? !working : false} />
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space wrap>
          <Text strong>{barTitle(workbench)}</Text>
          {kind === 'start' && (
            <>
              <Button type="primary" cta size="small" disabled={busy || working} onClick={start}>Xử lý phần mới</Button>
              <Text type="secondary" style={{ fontSize: 13 }}>{(workbench.warnResplit ?? 0) > 0 ? `${workbench.warnResplit} tập chưa viết sẽ được chia lại` : ''}</Text>
            </>
          )}
          {kind === 'interrupted' && <Button type="primary" cta size="small" disabled={working} onClick={resume}>Chạy tiếp</Button>}
          {kind === 'interrupted' && interrupted.goTo && <Button size="small" onClick={() => goTo(interrupted.goTo!)}>Mở Tổng quan truyện</Button>}
          {kind === 'wait' && run?.status === 'WAIT_SKELETON' && <Button size="small" onClick={() => goTo({ stage: 'skeleton' })}>Mở khung xương để duyệt / chốt</Button>}
          {kind === 'wait' && run?.status === 'WAIT_EPISODE_PLAN' && <>
            <Tooltip title={planApproveDisabledReason}><span><Button size="small" type="primary" cta disabled={working || !planApproval.can} onClick={approvePlan}>Chốt dàn tập & chạy tiếp</Button></span></Tooltip>
            <Button size="small" onClick={() => goTo({ stage: 'split' })}>Mở dàn tập để xem</Button>
            <Button size="small" type="link" onClick={() => goTo({ stage: 'split' })}>Xem mục thừa ở bước Chia tập</Button>
          </>}
          {(kind === 'interrupted' || kind === 'wait' || kind === 'running') && cancelBtn}
          {kind === 'done' && run && (
            <>
              <Text>Đã xong — {K} tập mới chờ viết</Text>
              <Button size="small" onClick={() => goTo({ stage: 'script' })}>Tới ⑤</Button>
            </>
          )}
          {kind === 'cancelled' && <Text>Đã huỷ phần mới</Text>}
          {(kind === 'done' || kind === 'cancelled') && run && <Button size="small" type="link" onClick={() => onDismiss(run.id)}>Đóng</Button>}
        </Space>
        {kind !== 'start' && kind !== 'cancelled' && (
          <ol style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', margin: 0, padding: 0, listStyle: 'none' }}>
            {steps.map((s) => (
              <li key={s.key + s.label} style={{ fontWeight: s.state === 'run' || s.state === 'wait' ? 600 : 400, color: s.state === 'todo' ? 'var(--wb-text-3)' : s.state === 'error' ? 'var(--wb-bad)' : undefined }}>
                <Tooltip title={s.state === 'skipped' ? 'Đã bỏ qua' : s.detail}>
                  <span>{STEP_ICON[s.state]} {s.label}{s.state === 'run' && progress ? ` — ${progress}` : ''}{s.state === 'skipped' ? ' (đã bỏ qua)' : ''}</span>
                </Tooltip>
              </li>
            ))}
          </ol>
        )}
        {run?.note && (kind === 'running' || kind === 'interrupted' || kind === 'done') && run.approvedAt && <Text type="secondary" style={{ fontSize: 13 }}>{run.note}</Text>}
        {kind === 'interrupted' && (
          <Strip tone="warn">{interrupted.text}</Strip>
        )}
      </Space>
    </div>
  );
};

export default AppendRunBar;
